import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { ClientErrorReportService } from '../services/client-error-report.service';
import { shouldReportClientError } from '../services/app-log-monitoring-rules';

/**
 * Global error handler that prevents known third-party library cleanup errors
 * from interrupting Angular's navigation lifecycle.
 *
 * Problem: ApexCharts (ng-apexcharts) throws "Cannot read properties of undefined
 * (reading 'node')" inside its ngOnDestroy when Angular removes the DOM before
 * calling child component destroy hooks. This exception propagates up through
 * executeOnDestroys → destroyLView → ViewRef.destroy, aborting the cleanup chain
 * and leaving the router-outlet empty (blank page requiring a second click).
 *
 * Fix: swallow that specific error when it originates from apexcharts, and let
 * Angular's navigation continue normally.
 *
 * It also recovers from stale lazy-route chunks: the browser keeps module URLs
 * that the server no longer serves — Vite re-optimizing its deps in dev
 * ("504 Outdated Optimize Dep"), or a deployment replacing hashed chunks in
 * production. Every navigation to that route then fails until a manual reload,
 * so we reload once, guarded against loops.
 *
 * Every other uncaught error is reported to the backend event log (screen
 * /admin/app-logs): an error a user saw and nobody else ever heard of is exactly the
 * "unidentified error" the log exists for. The report goes through HttpBackend, not
 * HttpClient, so it skips the interceptors — JwtInterceptor logs the user out on a
 * missing token or a 401, and a failing error report must never do that.
 */
@Injectable()
export class AppErrorHandler implements ErrorHandler {
  private static readonly RELOAD_GUARD_KEY = 'app.chunkReloadAt';
  private static readonly RELOAD_GUARD_MS = 15_000;

  private readonly lastReported = new Map<string, number>();

  constructor(private injector: Injector) {}

  handleError(error: unknown): void {
    if (this.isApexChartsNodeError(error)) {
      return;
    }
    console.error(error);

    if (this.isChunkLoadError(error)) {
      this.reloadOnce();
      return;
    }
    this.report(error);
  }

  /** Best effort, never throws: the error handler must not raise errors of its own. */
  private report(error: unknown): void {
    try {
      const err = error instanceof Error ? error : null;
      const message = err ? `${err.name}: ${err.message}` : String(error ?? '');
      if (!shouldReportClientError(message, this.lastReported, Date.now())) {
        return;
      }
      // Resolved lazily: the reporter needs AuthService, which depends on HttpClient,
      // which the ErrorHandler must not require at construction time.
      this.injector.get(ClientErrorReportService).report({
        message,
        name: err?.name || 'error',
        stack: err?.stack || '',
      });
    } catch {
      // Reporting is best effort.
    }
  }

  private isApexChartsNodeError(error: unknown): boolean {
    if (!(error instanceof TypeError)) {
      return false;
    }
    const msg = error.message ?? '';
    if (!msg.includes("Cannot read properties of undefined")) {
      return false;
    }
    const stack = error.stack ?? '';
    return stack.includes('apexcharts');
  }

  /** Dynamic import of a lazy route chunk that the server no longer serves. */
  private isChunkLoadError(error: unknown): boolean {
    const msg = error instanceof Error ? error.message : String(error ?? '');
    return (
      msg.includes('Failed to fetch dynamically imported module')
      || msg.includes('error loading dynamically imported module')
      || msg.includes('Importing a module script failed')
    );
  }

  /**
   * Reload at most once per guard window: if the chunk is genuinely missing,
   * reloading again would loop forever and hide the real error.
   */
  private reloadOnce(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const now = Date.now();
    const last = Number(window.sessionStorage?.getItem(AppErrorHandler.RELOAD_GUARD_KEY) ?? 0);
    if (last && now - last < AppErrorHandler.RELOAD_GUARD_MS) {
      return;
    }

    try {
      window.sessionStorage?.setItem(AppErrorHandler.RELOAD_GUARD_KEY, String(now));
    } catch {
      // Private mode / storage disabled: reload anyway, the loop risk is on the user's side.
    }
    window.location.reload();
  }
}
